import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule, NgModel } from '@angular/forms';
import {
  NOTICE_MILESTONES,
  NOTICE_RESPONSE_CODES,
  NOTICE_ROLES,
  NoticePolicy,
  NoticeRecipientRule,
  parseNoticePolicy,
} from './notice-policy';
export interface NoticePolicyChangeRequest {
  context: string;
  value: string;
}

@Component({
  selector: 'phms-notice-policy-editor',
  standalone: true,
  imports: [FormsModule],
  template: `@if (policyView; as view) {
    @if (view.policy; as p) {
      <label
        >Notice milestone
        <select
          [disabled]="disabled"
          [ngModel]="p.noticeMilestone"
          #milestoneModel="ngModel"
          (ngModelChange)="
            set('noticeMilestone', $event, view.context, milestoneModel)
          "
        >
          @for (v of milestones; track v) {
            <option [value]="v">{{ milestone(v) }}</option>
          }
        </select></label
      >
      <label
        ><input
          type="checkbox"
          [disabled]="disabled"
          [ngModel]="p.responseRequired"
          #responseRequiredModel="ngModel"
          (ngModelChange)="
            set('responseRequired', $event, view.context, responseRequiredModel)
          "
        />
        Require responses</label
      >
      <p>
        Responses are optional by default. A response records communication,
        including a decision not to attend; it does not approve attendance.
      </p>
      <fieldset>
        <legend>Accepted responses</legend>
        @for (c of responses; track c) {
          <label
            ><input
              type="checkbox"
              [disabled]="disabled"
              [checked]="p.acceptedResponseCodes.includes(c)"
              (change)="
                responseCode(
                  c,
                  $any($event.target).checked,
                  view.context,
                  $any($event.target)
                )
              "
            />{{ label(c) }}</label
          >
        }
      </fieldset>
      <fieldset>
        <legend>Recipient rules</legend>
        @for (role of roles; track role) {
          <label
            ><input
              type="checkbox"
              [disabled]="disabled"
              [checked]="has(role)"
              (change)="
                toggleRole(
                  role,
                  $any($event.target).checked,
                  view.context,
                  $any($event.target)
                )
              "
            />{{ label(role) }}</label
          >
          @if (rule(role); as r) {
            <div class="rule">
              <label
                >Notice override
                <select
                  [disabled]="disabled"
                  [ngModel]="r.noticeMilestone ?? ''"
                  #noticeOverrideModel="ngModel"
                  (ngModelChange)="
                    override(
                      role,
                      'noticeMilestone',
                      $event,
                      view.context,
                      noticeOverrideModel
                    )
                  "
                >
                  <option value="">Use group milestone</option>
                  @for (v of milestones; track v) {
                    <option [value]="v">{{ milestone(v) }}</option>
                  }
                </select></label
              ><label
                >Response override
                <select
                  #responseOverrideModel="ngModel"
                  [disabled]="disabled"
                  [ngModel]="
                    r.responseRequired === undefined
                      ? ''
                      : r.responseRequired
                        ? 'yes'
                        : 'no'
                  "
                  (ngModelChange)="
                    override(
                      role,
                      'responseRequired',
                      $event,
                      view.context,
                      responseOverrideModel
                    )
                  "
                >
                  <option value="">Use group response policy</option>
                  <option value="yes">Required</option>
                  <option value="no">Optional</option>
                </select></label
              >
              <label
                ><input
                  type="checkbox"
                  [disabled]="disabled"
                  [checked]="!!r.acceptedResponseCodes"
                  (change)="
                    ruleResponses(
                      role,
                      $any($event.target).checked,
                      view.context,
                      $any($event.target)
                    )
                  "
                />Override accepted response codes</label
              >
              @if (r.acceptedResponseCodes; as selected) {
                @for (c of responses; track c) {
                  <label
                    ><input
                      type="checkbox"
                      [disabled]="disabled"
                      [checked]="selected.includes(c)"
                      (change)="
                        ruleResponseCode(
                          role,
                          c,
                          $any($event.target).checked,
                          view.context,
                          $any($event.target)
                        )
                      "
                    />{{ label(c) }}</label
                  >
                }
              }
            </div>
          }
        }
      </fieldset>
      <label
        ><input
          type="checkbox"
          [disabled]="disabled"
          [ngModel]="p.allowReviewedNone"
          #reviewedNoneModel="ngModel"
          (ngModelChange)="
            set('allowReviewedNone', $event, view.context, reviewedNoneModel)
          "
        />
        Allow reviewed no applicable recipients</label
      >
      <p>
        Requires complete authorized resolution, a recorded reason and no
        previously outstanding recipients.
      </p>
    } @else {
      <p role="alert">
        The saved notice policy is unavailable. Its original settings are
        preserved.
      </p>
    }
  }`,
  styles: [
    `
      :host {
        display: grid;
        gap: 0.7rem;
      }
      label {
        display: block;
        margin: 0.35rem 0;
      }
      select {
        display: block;
        max-width: 100%;
        min-height: 2.2rem;
      }
      .rule {
        margin: 0 0 0.8rem 1.4rem;
      }
      fieldset {
        min-width: 0;
      }
      p {
        font-size: 0.85rem;
        overflow-wrap: anywhere;
      }
    `,
  ],
})
export class NoticePolicyEditorComponent {
  @Input() value: unknown;
  @Input() disabled = false;
  @Input() changeContext: string | null = null;
  @Input() currentChangeContext: (() => string | null) | null = null;
  @Input() currentValue: (() => unknown) | null = null;
  @Output() valueChange = new EventEmitter<string>();
  @Output() policyChangeRequested =
    new EventEmitter<NoticePolicyChangeRequest>();
  private emitting: NoticePolicyChangeRequest | null = null;
  private restoring = false;
  get policyView() {
    return { policy: this.policy(), context: this.changeContext };
  }
  private currentPolicy() {
    return parseNoticePolicy(
      this.currentValue ? this.currentValue() : this.value,
      true,
    );
  }
  isCurrentPolicyChangeRequest(request: NoticePolicyChangeRequest): boolean {
    return this.emitting === request && this.current(request.context);
  }
  private current(context: string | null | undefined): boolean {
    return (
      !this.disabled &&
      !this.restoring &&
      (!this.currentChangeContext ||
        (!!context &&
          context === this.changeContext &&
          context === this.currentChangeContext()))
    );
  }
  private restoreModel(model: NgModel | undefined, value: unknown): void {
    if (!this.currentChangeContext || !model || this.restoring) return;
    this.restoring = true;
    try {
      model.control.setValue(value, { emitEvent: false });
    } finally {
      this.restoring = false;
    }
  }
  private restoreCheckbox(
    control: HTMLInputElement | undefined,
    checked: boolean,
  ): void {
    if (this.currentChangeContext && control) control.checked = checked;
  }
  readonly roles = NOTICE_ROLES;
  readonly responses = NOTICE_RESPONSE_CODES;
  readonly milestones = NOTICE_MILESTONES;
  policy() {
    return parseNoticePolicy(this.value, true);
  }
  label(v: string) {
    return v.replaceAll('_', ' ');
  }
  milestone(v: string) {
    return (
      (
        {
          sent: 'Sent recorded',
          prepared: 'Copy prepared',
          delivered: 'Delivered recorded',
          none: 'None — response collection',
        } as Record<string, string>
      )[v] || v
    );
  }
  private emit(p: NoticePolicy, context?: string | null): void {
    if (!this.current(context)) return;
    const value = JSON.stringify(p);
    if (!this.currentChangeContext) {
      this.valueChange.emit(value);
      return;
    }
    if (!context) return;
    const request = { context, value };
    this.emitting = request;
    try {
      this.policyChangeRequested.emit(request);
    } finally {
      this.emitting = null;
    }
  }
  set<K extends keyof NoticePolicy>(
    key: K,
    value: NoticePolicy[K],
    context?: string | null,
    model?: NgModel,
  ) {
    if (this.restoring) return;
    const p = this.policy();
    if (p) this.emit({ ...p, [key]: value }, context);
    this.restoreModel(model, this.currentPolicy()?.[key]);
  }
  has(role: (typeof NOTICE_ROLES)[number]) {
    return (
      this.policy()?.recipientRules.some((r) => r.roleCode === role) ?? false
    );
  }
  rule(role: (typeof NOTICE_ROLES)[number]) {
    return this.policy()?.recipientRules.find((r) => r.roleCode === role);
  }
  toggleRole(
    role: (typeof NOTICE_ROLES)[number],
    checked: boolean,
    context?: string | null,
    control?: HTMLInputElement,
  ) {
    const p = this.policy();
    if (p)
      this.emit(
        {
          ...p,
          recipientRules: checked
            ? [...p.recipientRules, { roleCode: role }]
            : p.recipientRules.filter((r) => r.roleCode !== role),
        },
        context,
      );
    this.restoreCheckbox(
      control,
      this.currentPolicy()?.recipientRules.some((r) => r.roleCode === role) ??
        false,
    );
  }
  responseCode(
    code: string,
    checked: boolean,
    context?: string | null,
    control?: HTMLInputElement,
  ) {
    const p = this.policy();
    if (p)
      this.emit(
        {
          ...p,
          acceptedResponseCodes: checked
            ? [...p.acceptedResponseCodes, code]
            : p.acceptedResponseCodes.filter((c) => c !== code),
        },
        context,
      );
    this.restoreCheckbox(
      control,
      this.currentPolicy()?.acceptedResponseCodes.includes(code) ?? false,
    );
  }
  ruleResponses(
    role: (typeof NOTICE_ROLES)[number],
    enabled: boolean,
    context?: string | null,
    control?: HTMLInputElement,
  ) {
    const p = this.policy();
    if (p)
      this.emit(
        {
          ...p,
          recipientRules: p.recipientRules.map((r) => {
            if (r.roleCode !== role) return r;
            const next = { ...r };
            if (enabled)
              next.acceptedResponseCodes = [...p.acceptedResponseCodes];
            else delete next.acceptedResponseCodes;
            return next;
          }),
        },
        context,
      );
    this.restoreCheckbox(
      control,
      !!this.currentPolicy()?.recipientRules.find((r) => r.roleCode === role)
        ?.acceptedResponseCodes,
    );
  }
  ruleResponseCode(
    role: (typeof NOTICE_ROLES)[number],
    code: string,
    checked: boolean,
    context?: string | null,
    control?: HTMLInputElement,
  ) {
    const p = this.policy();
    if (p)
      this.emit(
        {
          ...p,
          recipientRules: p.recipientRules.map((r) =>
            r.roleCode !== role
              ? r
              : {
                  ...r,
                  acceptedResponseCodes: checked
                    ? [...(r.acceptedResponseCodes ?? []), code]
                    : r.acceptedResponseCodes?.filter((c) => c !== code),
                },
          ),
        },
        context,
      );
    this.restoreCheckbox(
      control,
      this.currentPolicy()
        ?.recipientRules.find((r) => r.roleCode === role)
        ?.acceptedResponseCodes?.includes(code) ?? false,
    );
  }
  override(
    role: (typeof NOTICE_ROLES)[number],
    key: keyof Omit<NoticeRecipientRule, 'roleCode'>,
    value: string,
    context?: string | null,
    model?: NgModel,
  ) {
    if (this.restoring) return;
    const p = this.policy();
    if (p)
      this.emit(
        {
          ...p,
          recipientRules: p.recipientRules.map((r) => {
            if (r.roleCode !== role) return r;
            const next = { ...r };
            if (value === '') delete next[key];
            else if (key === 'responseRequired')
              next.responseRequired = value === 'yes';
            else if (key === 'noticeMilestone')
              next.noticeMilestone =
                value as NoticeRecipientRule['noticeMilestone'];
            return next;
          }),
        },
        context,
      );
    const rule = this.currentPolicy()?.recipientRules.find(
      (r) => r.roleCode === role,
    );
    this.restoreModel(
      model,
      key === 'responseRequired'
        ? rule?.responseRequired === undefined
          ? ''
          : rule.responseRequired
            ? 'yes'
            : 'no'
        : (rule?.noticeMilestone ?? ''),
    );
  }
}
